'use client'

import { extractId } from '@/utils/common.util'
import { useRowLabel } from '@payloadcms/ui'
import { useEffect, useRef, useState } from 'react'

type MentorCheckData = {
  node?: number | { id: number; header?: string; externalId?: string }
  status?: 'submitted' | 'approved' | 'needs-revision'
  studentSubmission?: {
    submittedAt?: string
  }
}

export const MentorCheckRowLabel = () => {
  const { data } = useRowLabel<MentorCheckData>()
  const [nodeTitle, setNodeTitle] = useState<string>(`Node #${extractId(data?.node) ?? 'Unknown'}`)
  const fetchedRef = useRef<number | null>(null)

  useEffect(() => {
    const fetchNodeTitle = async () => {
      if (!data?.node) {
        setNodeTitle('Unknown Node')
        return
      }

      // If node is populated as object
      if (typeof data.node === 'object' && data.node !== null) {
        const title = data.node.header || data.node.externalId || 'Unknown Node'
        setNodeTitle(title)
        return
      }

      // If node is just an ID, fetch it
      if (typeof data.node === 'number') {
        // Prevent duplicate fetches
        if (fetchedRef.current === data.node) {
          return
        }
        fetchedRef.current = data.node

        try {
          const response = await fetch(`/api/skill-tree-nodes/${data.node}`)
          if (response.ok) {
            const nodeData = await response.json()
            setNodeTitle(nodeData.header || nodeData.externalId || `Node #${data.node}`)
          } else {
            setNodeTitle(`Node #${data.node}`)
          }
        } catch (error) {
          console.error('Error fetching node:', error)
          setNodeTitle(`Node #${data.node}`)
        }
      }
    }

    fetchNodeTitle()
  }, [data?.node])

  const status = data?.status || 'submitted'
  const submittedAt = data?.studentSubmission?.submittedAt

  // Create a readable status label
  const statusLabels = {
    submitted: '⏳ Awaiting Review',
    approved: '✅ Approved',
    'needs-revision': '🔄 Needs Revision',
  }

  const statusLabel = statusLabels[status as keyof typeof statusLabels] || status

  // Format submission date if available
  const submissionText = submittedAt
    ? ` (submitted ${new Date(submittedAt).toLocaleDateString()})`
    : ''

  return (
    <div>
      {nodeTitle} - {statusLabel}
      {submissionText}
    </div>
  )
}
